import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ErrorOverlay } from './components/ErrorOverlay';
import { logException } from './lib/eventsLogger';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  isDevelopment = process.env.NODE_ENV === 'development';

  public static getDerivedStateFromError(error: Error): State {
    logException(error, {
      errorBoundary: 'ErrorBoundary',
      caughtIn: 'getDerivedStateFromError',
    });

    return {
      hasError: true,
      error,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logException(error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: 'ErrorBoundary',
      caughtIn: 'componentDidCatch',
      errorInfo,
    });

    console.error('Uncaught error:', error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      return (
        <ErrorOverlay
          title="An unexpected error occurred."
          subtitle="We're working to fix it. Try refreshing or returning to the homepage."
          error={this.isDevelopment ? this.state.error : undefined}
        />
      );
    }

    return this.props.children;
  }
}
